import React from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, LocateFixed } from 'lucide-react';
import type { useNearMe } from '../../hooks/useNearMe';

type NearMe = ReturnType<typeof useNearMe>;

/** "Near me" toggle: asks for the location on tap; tap again to stop. */
export function NearMeButton({ nearMe }: { nearMe: NearMe }) {
  const { t } = useTranslation();
  const active = nearMe.status === 'active';
  return (
    <button
      type="button"
      onClick={active ? nearMe.clear : nearMe.request}
      disabled={nearMe.status === 'locating'}
      aria-pressed={active}
      className={`shrink-0 inline-flex items-center gap-1.5 rounded-full border px-3 min-h-10 text-sm font-medium transition-colors ${
        active ? 'bg-weekday-600 border-weekday-600 text-paper' : 'bg-paper border-ink/15 text-ink hover:border-ink/40'
      }`}
    >
      {nearMe.status === 'locating' ? (
        <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
      ) : (
        <LocateFixed className="w-4 h-4" aria-hidden />
      )}
      {t('directory.map.nearMe')}
    </button>
  );
}

const MESSAGE_KEYS: Partial<Record<NearMe['status'], string>> = {
  locating: 'directory.map.locating',
  denied: 'directory.map.locationDenied',
  unavailable: 'directory.map.locationUnavailable',
  insecure: 'directory.map.locationInsecure',
};

/** What Near me is doing: finding you, why it can't, or "sorted by distance". */
export function NearMeNotice({ nearMe }: { nearMe: NearMe }) {
  const { t } = useTranslation();
  const key = MESSAGE_KEYS[nearMe.status];
  if (key) {
    return (
      <p className={`text-sm ${nearMe.status === 'locating' ? 'text-ash' : 'text-danger-600'}`} role="status">
        {t(key)}
      </p>
    );
  }
  if (nearMe.status === 'active') {
    return (
      <p className="text-xs text-ash" role="status">
        {t('directory.map.sortedByDistance')} ·{' '}
        <button type="button" onClick={nearMe.clear} className="underline text-ink">
          {t('directory.map.clearNearMe')}
        </button>
      </p>
    );
  }
  return null;
}
